import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, Grid } from 'lucide-react';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import categories from '../../data/categories';

/**
 * Компонент Header - навигационная панель сайта
 *
 * @param {boolean} darkMode - Текущий режим темы (светлая/темная)
 * @param {function} toggleDarkMode - Функция переключения темы
 */
const Header = ({ darkMode, toggleDarkMode }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const sideMenuContainerRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Закрытие меню при клике вне
  useEffect(() => {
    function handleClickOutside(event) {
      if (sideMenuContainerRef.current && !sideMenuContainerRef.current.contains(event.target)) {
        setIsSideMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMobileMenu = () => setIsMobileMenuOpen(prev => !prev);
  const toggleSideMenu = () => setIsSideMenuOpen(prev => !prev);

  const handleSearch = e => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (query) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setSearchQuery('');
      setIsMobileMenuOpen(false);
      setIsSideMenuOpen(false);
    }
  };

  return (
      <>
        <header
            className="sticky top-0 z-20 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              {/* Логотип */}
              <div className="flex items-center">
                <Link to="/" className="flex-shrink-0 flex items-center">
                  <div className="p-2 rounded-lg mr-2">
                    <img src="/logo192.png" alt="Logo" className="h-8 w-8"/>
                  </div>
                  <span className="font-bold text-xl text-indigo-600 dark:text-indigo-400">Gensio</span>
                </Link>
              </div>

              {/* Поиск - десктоп */}
              <div className="hidden md:block flex-1 max-w-xl mx-4">
                <form onSubmit={handleSearch} className="relative">
                  <input
                      type="text"
                      className="block w-full pl-10 pr-10 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                      placeholder="Найти инструмент..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                  />
                  <button type="submit"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-auto">
                    <Search className="h-5 w-5 text-gray-500"/>
                  </button>
                </form>
              </div>

              {/* Действия */}
              <div className="flex items-center space-x-2">
                {/* Боковое меню категорий - десктоп */}
                <div className="hidden md:block relative" ref={sideMenuContainerRef}>
                  <button
                      onClick={toggleSideMenu}
                      className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Grid className="h-6 w-6"/>
                  </button>

                  {/* Боковое меню - десктоп, вложено для правильной работы click outside */}
                  {isSideMenuOpen && (
                      <aside
                          className="fixed top-0 right-0 h-full w-80 bg-white dark:bg-gray-900 shadow-lg z-50 animate-slide-left">
                        <div className="flex flex-col p-6 space-y-4 overflow-y-auto bg-white">
                          <div className="flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Категории</h2>
                            <button onClick={toggleSideMenu}
                                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                              <X className="h-5 w-5 text-gray-500"/>
                            </button>
                          </div>
                          <div className="grid grid-cols-1 gap-3">
                            {categories.map(cat => (
                                <Link
                                    key={cat.id}
                                    to={`/category/${cat.slug}`}
                                    className="flex items-center p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    onClick={() => setIsSideMenuOpen(false)}
                                >
                                  <Icon name={cat.iconName} size="lg"
                                        className={`mr-3 ${location.pathname === `/category/${cat.slug}` ? 'text-indigo-600' : 'text-gray-500 dark:text-gray-400'}`}/>
                                  <div>
                                    <p className="font-medium capitalize text-gray-900 dark:text-gray-100">{cat.name}</p>

                                  </div>
                                </Link>
                            ))}
                          </div>
                        </div>
                      </aside>
                  )}
                </div>

                {/* Тема */}
                <button
                    className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    onClick={toggleDarkMode}
                >
                  {darkMode ? <Icon name="sun" className="text-amber-500" size="md"/> : <Icon name="moon" size="md"/>}
                </button>

                {/* Мобильное меню */}
                <div className="md:hidden">
                  <button
                      className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      onClick={toggleMobileMenu}
                  >
                    {isMobileMenuOpen ? <X className="h-5 w-5"/> : <Menu className="h-5 w-5"/>}
                  </button>
                </div>
              </div>
            </div>

            {/* Поиск - мобильный */}
            <div className="md:hidden py-2">
              <form onSubmit={handleSearch} className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400"/>
                </div>
                <input
                    type="text"
                    className="block w-full pl-10 pr-10 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                    placeholder="Найти инструмент..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-auto">
                  <Search className="h-5 w-5 text-gray-500"/>
                </button>
              </form>
            </div>
          </div>
        </header>

        {/* Мобильное меню */}
        {isMobileMenuOpen && (
            <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
              <div
                  ref={mobileMenuRef}
                  className="absolute top-0 left-0 h-full w-64 bg-white dark:bg-gray-900 shadow-xl overflow-y-auto animate-slide-left-mobile"
              >
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Меню</h2>
                    <button onClick={toggleMobileMenu}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                      <X className="h-5 w-5 text-gray-500"/>
                    </button>
                  </div>
                  <nav className="space-y-4">
                    <Link
                        to="/"
                        className="block text-base font-medium text-gray-900 dark:text-white hover:underline"
                        onClick={toggleMobileMenu}
                    >
                      Главная
                    </Link>
                    <div className="border-t border-gray-200 dark:border-gray-800"/>
                    {categories.map(category => (
                        <Link
                            key={category.id}
                            to={`/category/${category.slug}`}
                            className="flex items-center p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            onClick={toggleMobileMenu}
                        >
                          <Icon name={category.iconName} size="md" className="mr-3 text-gray-500 dark:text-gray-400"/>
                          <span className="capitalize text-gray-900 dark:text-white">{category.name}</span>
                        </Link>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
        )}

        <style jsx global>{`
          @keyframes slide-right {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }

          @keyframes slide-left {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }

          @keyframes slide-left-mobile {
            from {
              transform: translateX(-100%);
            }
            to {
              transform: translateX(0);
            }
          }

          .animate-slide-left {
            animation: slide-left 0.3s ease-out forwards;
          }

          .animate-slide-left-mobile {
            animation: slide-left-mobile 0.3s ease-out forwards;
          }
        `}</style>
      </>
  );
};

export default Header;
